import { useQuery } from '@tanstack/react-query'
import { useUser } from 'src/services/user/hooks'
import { organizationFeatureConfigQueries } from 'src/services/organization-feature-config'
import { organizationQueries } from 'src/services/user'

/**
 * Returns the feature config for the current user's first organization.
 */
export function useOrgFeatureConfig() {
  const { data: user } = useUser()
  const orgId = user?.memberships?.[0]?.organizationId
  return useQuery(organizationFeatureConfigQueries.retrieve(orgId))
}

export type AiSearchMode = 'vector' | 'query_builder'

/**
 * Resolves which AI Search modes the current user's org may use.
 *
 * The AI search endpoints are beta-gated on the backend, and each search path
 * has its own flag. A mode is only enabled when the org is beta AND the
 * matching flag is on, so a non-beta org (or a beta org with both flags off)
 * gets no modes — and therefore no entry point that would 403.
 */
export function useAiSearchModes() {
  const { data: user } = useUser()
  const orgId = user?.memberships?.[0]?.organizationId
  const { data: featureConfig } = useQuery(organizationFeatureConfigQueries.retrieve(orgId))
  const { data: organizations } = useQuery(organizationQueries.list())

  const organization = organizations?.results?.find((org) => org.id === orgId)
  const isBeta = Boolean(organization?.isBeta)

  const vectorEnabled = isBeta && Boolean(featureConfig?.aiVectorSearchEnabled)
  const queryBuilderEnabled = isBeta && Boolean(featureConfig?.aiQueryBuilderEnabled)
  const anyModeEnabled = vectorEnabled || queryBuilderEnabled

  const onlyMode: AiSearchMode | null =
    vectorEnabled && !queryBuilderEnabled
      ? 'vector'
      : queryBuilderEnabled && !vectorEnabled
      ? 'query_builder'
      : null

  return { isBeta, vectorEnabled, queryBuilderEnabled, anyModeEnabled, onlyMode }
}
